import { b64ToBytes as fromB64, bytesToB64 as toB64, zeroBytes } from "./bytes";
import { deriveWorkspaceKey } from "./hkdf";
import { openEnvelope, sealEnvelope } from "./envelope";
import { MasterKeyManager } from "../crypto/masterKey";

/**
 * In-memory workspace-key (WK) cache. Keys are non-extractable CryptoKeys and are never
 * written to localStorage. IndexedDB persistence / logout wiring is TASK-UI-VFS-003-2.
 */
const cache = new Map<string, CryptoKey>();

export const wkStore = {
  get(workspaceId: string): CryptoKey | undefined {
    return cache.get(workspaceId);
  },
  set(workspaceId: string, wk: CryptoKey): void {
    cache.set(workspaceId, wk);
  },
  delete(workspaceId: string): void {
    cache.delete(workspaceId);
  },
  clear(): void {
    cache.clear();
  },
};

// ── Workspace-key lifecycle (TASK-UI-VFS-003-2) ────────────────────────────────
//
// The master key (MK) is NEVER stored: it is only read through {@link MasterKeySource}
// (the KMS client-keys flow that already holds it in memory) while a WK is derived, and the
// raw bytes are zeroed right after. WK is cached in memory and (optionally) persisted per
// workspace as a NON-extractable CryptoKey in IndexedDB, so a reload does not need the MK
// again. Everything is wiped on logout (authStore) and on non-bfcache `pagehide`.

export const WK_DB_NAME = "vnp-vfs-wk";
const WK_STORE = "keys";
const KEY_CHECK_PLAINTEXT = "vfs-key-check";

export type WorkspaceKeyErrorCode =
  "mk_unavailable" | "wrong_key" | "bad_key_check";

export class WorkspaceKeyError extends Error {
  readonly code: WorkspaceKeyErrorCode;
  constructor(code: WorkspaceKeyErrorCode, message: string) {
    super(message);
    this.name = "WorkspaceKeyError";
    this.code = code;
  }
}

/** Supplies raw master-key bytes for the duration of one derivation. Caller zeroes them. */
export interface MasterKeySource {
  getMasterKeyBytes(): Promise<Uint8Array>;
}

/**
 * Default source. Investigated 2026-10: `MasterKeyManager` (shared/lib/crypto/masterKey.ts)
 * holds the MK as an AES-GCM CryptoKey imported NON-extractable with usages
 * ["encrypt","decrypt"] (decryptAndLoad). WebCrypto only accepts an HKDF-algorithm key as the
 * base key of deriveKey/deriveBits, and an AES-GCM key cannot be converted or exported, so a
 * workspace key cannot be derived from it through any public API (MasterKeyManager's own
 * deriveSessionKey/deriveConnectionKey call exportKey and therefore only work while the MK
 * is still the extractable bootstrap key). Exposing raw MK bytes would weaken the design, so until
 * MasterKeyManager gains a dedicated `deriveWorkspaceKey` (owned by the privacy feature),
 * opening a workspace for the first time reports `mk_unavailable` instead of faking a key.
 */
/**
 * Default source (VFS-003). Uses MasterKeyManager.getMasterKeyBytesForVFS() to supply
 * raw MK bytes for workspace-key derivation. Requires decryptAndLoad to have been called
 * (login path); throws mk_unavailable if the user has not logged in with their password yet.
 */
const masterKeySource: MasterKeySource = {
  async getMasterKeyBytes(): Promise<Uint8Array> {
    if (!MasterKeyManager.isLoaded()) {
      throw new WorkspaceKeyError(
        "mk_unavailable",
        "Khoá chủ (MK) chưa có trong phiên này — đăng nhập bằng mật khẩu trước"
      );
    }
    return MasterKeyManager.getMasterKeyBytesForVFS(); // caller zeroes
  },
};

export interface WkPersistence {
  put(workspaceId: string, wk: CryptoKey): Promise<void>;
  get(workspaceId: string): Promise<CryptoKey | undefined>;
  delete(workspaceId: string): Promise<void>;
  clear(): Promise<void>;
}

export class MemoryWkPersistence implements WkPersistence {
  private readonly map = new Map<string, CryptoKey>();
  async put(id: string, k: CryptoKey) {
    this.map.set(id, k);
  }
  async get(id: string) {
    return this.map.get(id);
  }
  async delete(id: string) {
    this.map.delete(id);
  }
  async clear() {
    this.map.clear();
  }
}

export class IndexedDbWkPersistence implements WkPersistence {
  private dbPromise: Promise<IDBDatabase> | null = null;
  private db(): Promise<IDBDatabase> {
    if (!this.dbPromise) {
      this.dbPromise = new Promise((resolve, reject) => {
        const open = indexedDB.open(WK_DB_NAME, 1);
        open.onupgradeneeded = () => {
          open.result.createObjectStore(WK_STORE);
        };
        open.onsuccess = () => resolve(open.result);
        open.onerror = () => reject(open.error);
      });
      this.dbPromise.catch(() => {
        this.dbPromise = null;
      });
    }
    return this.dbPromise;
  }
  private async run<T>(
    mode: IDBTransactionMode,
    fn: (s: IDBObjectStore) => IDBRequest<T>
  ): Promise<T> {
    const db = await this.db();
    const req = fn(db.transaction(WK_STORE, mode).objectStore(WK_STORE));
    return new Promise((resolve, reject) => {
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => reject(req.error);
    });
  }
  async put(id: string, k: CryptoKey) {
    await this.run("readwrite", (s) => s.put(k, id));
  }
  async get(id: string) {
    return (await this.run("readonly", (s) => s.get(id))) as
      CryptoKey | undefined;
  }
  async delete(id: string) {
    await this.run("readwrite", (s) => s.delete(id));
  }
  async clear() {
    await this.run("readwrite", (s) => s.clear());
  }
}

/**
 * key_check = base64( wrappedDek(40) || nonce || AES-GCM("vfs-key-check") ) sealed under WK.
 * NOTE: the SOL says "AES-GCM('vfs-key-check', WK)" but WK is an AES-KW key, so the check is
 * an envelope; the byte layout is a UI-side convention until the backend fixes one.
 */
export async function makeKeyCheck(
  wk: CryptoKey,
  keyVersion = 1
): Promise<string> {
  const env = await sealEnvelope(
    new TextEncoder().encode(KEY_CHECK_PLAINTEXT),
    wk,
    keyVersion
  );
  const out = new Uint8Array(env.wrappedDek.length + env.ciphertext.length);
  out.set(env.wrappedDek, 0);
  out.set(env.ciphertext, env.wrappedDek.length);
  return toB64(out);
}

const WRAPPED_DEK_BYTES = 40;

/** True iff `wk` opens `keyCheckB64`. Never throws for a wrong key. */
export async function verifyKeyCheck(
  wk: CryptoKey,
  keyCheckB64: string
): Promise<boolean> {
  let raw: Uint8Array;
  try {
    raw = fromB64(keyCheckB64);
  } catch {
    throw new WorkspaceKeyError("bad_key_check", "key_check không hợp lệ");
  }
  if (raw.length <= WRAPPED_DEK_BYTES) {
    throw new WorkspaceKeyError("bad_key_check", "key_check không hợp lệ");
  }
  try {
    const plain = await openEnvelope(
      {
        keyVersion: 1,
        wrappedDek: raw.slice(0, WRAPPED_DEK_BYTES),
        ciphertext: raw.slice(WRAPPED_DEK_BYTES),
      },
      wk
    );
    return new TextDecoder().decode(plain) === KEY_CHECK_PLAINTEXT;
  } catch {
    return false;
  }
}

export interface WorkspaceKeyManager {
  /**
   * Returns the verified WK for a workspace: memory -> persistence -> derive from MK.
   * A cached key that fails key_check is dropped; a derived key that fails is NOT cached
   * and the call rejects with `wrong_key` (callers must not loop on it).
   */
  open(
    workspaceId: string,
    keyCheckB64: string,
    keyVersion?: number
  ): Promise<CryptoKey>;
  /** Derive (without caching) the WK of a given version, e.g. the rotation target. */
  derive(workspaceId: string, keyVersion: number): Promise<CryptoKey>;
  /** After a finished rotation: replace the cached/persisted key with the new WK. */
  adopt(workspaceId: string, wk: CryptoKey): Promise<void>;
  lock(workspaceId: string): Promise<void>;
  /** Logout / pagehide: wipe memory + persistence. */
  clearAll(): Promise<void>;
  isUnlocked(workspaceId: string): boolean;
}

export function createWorkspaceKeyManager(opts: {
  persistence: WkPersistence;
  masterKeySource?: MasterKeySource;
}): WorkspaceKeyManager {
  // Default: masterKeySource (real). Throws mk_unavailable if MK is not loaded yet.
  const source = opts.masterKeySource ?? masterKeySource;
  const mem = new Map<string, CryptoKey>();

  const safe = async (fn: () => Promise<unknown>) => {
    try {
      await fn();
    } catch {
      /* storage unavailable (private mode): memory cache still works */
    }
  };

  const derive = async (workspaceId: string, keyVersion: number) => {
    const mk = await source.getMasterKeyBytes();
    try {
      return await deriveWorkspaceKey(mk, workspaceId, keyVersion);
    } finally {
      zeroBytes(mk);
    }
  };

  return {
    isUnlocked: (id) => mem.has(id),

    derive,

    async adopt(workspaceId, wk) {
      mem.set(workspaceId, wk);
      await safe(() => opts.persistence.put(workspaceId, wk));
    },

    async open(workspaceId, keyCheckB64, keyVersion = 1) {
      const cached = mem.get(workspaceId);
      if (cached && (await verifyKeyCheck(cached, keyCheckB64))) return cached;
      mem.delete(workspaceId);

      let stored: CryptoKey | undefined;
      try {
        stored = await opts.persistence.get(workspaceId);
      } catch {
        stored = undefined;
      }
      if (stored) {
        if (await verifyKeyCheck(stored, keyCheckB64)) {
          mem.set(workspaceId, stored);
          return stored;
        }
        await safe(() => opts.persistence.delete(workspaceId));
      }

      const wk = await derive(workspaceId, keyVersion);
      if (!(await verifyKeyCheck(wk, keyCheckB64))) {
        throw new WorkspaceKeyError(
          "wrong_key",
          "Khoá không khớp với workspace này"
        );
      }
      mem.set(workspaceId, wk);
      await safe(() => opts.persistence.put(workspaceId, wk));
      return wk;
    },

    async lock(workspaceId) {
      mem.delete(workspaceId);
      await safe(() => opts.persistence.delete(workspaceId));
    },

    async clearAll() {
      mem.clear();
      wkStore.clear();
      await safe(() => opts.persistence.clear());
    },
  };
}

let wkManager: WorkspaceKeyManager | null = null;
let wkPagehideInstalled = false;

/** Replace the process-wide manager (tests). Pass null to reset. */
export function setWorkspaceKeyManagerForTests(
  m: WorkspaceKeyManager | null
): void {
  wkManager = m;
}

export function getWorkspaceKeyManager(): WorkspaceKeyManager {
  if (!wkManager) {
    wkManager = createWorkspaceKeyManager({
      persistence:
        typeof indexedDB !== "undefined"
          ? new IndexedDbWkPersistence()
          : new MemoryWkPersistence(),
      masterKeySource, // VFS-003: use MasterKeyManager raw bytes
    });
  }
  if (!wkPagehideInstalled && typeof window !== "undefined") {
    wkPagehideInstalled = true;
    window.addEventListener("pagehide", (e: PageTransitionEvent) => {
      if (e.persisted) return;
      void wkManager?.clearAll();
    });
  }
  return wkManager;
}

/** Wipe every workspace key. Safe to call anywhere (logout). */
export function clearAllWorkspaceKeys(): Promise<void> {
  return getWorkspaceKeyManager().clearAll();
}
